import { useCallback, useEffect, useMemo, useState } from "react";
// import { createBrowserInspector } from "@statelyai/inspect";
import { useMachine } from "@xstate/react";

import {
  getActiveTournamentMatch,
  getTournamentResults,
  getTournamentStandings,
  isTournamentDone,
  subscribeToTournament,
  type TournamentActorRef,
} from "@/features/tournament/tournamentAdapters";
import { useActivities, usePlayerList, usePlayers } from "@/store/selectors";
import { useStore } from "@/store/store";
import type { Player } from "@/types";
import { isTournament } from "@/types";
import { type ActivityId, type PlayerId } from "@/types/branded";

import {
  createTeamsForActivity,
  groupByRank,
  matchFlowMachine,
  type MatchFlowPhase,
  type Teams,
} from "./matchFlow.machine";

// const inspect = createBrowserInspector();

export const useMatchFlow = () => {
  const playersById = usePlayers();
  const playerList = usePlayerList();
  const activitiesById = useActivities();
  const recordMatch = useStore((s) => s.recordMatch);

  const [snapshot, send] = useMachine(matchFlowMachine, {
    // inspect: inspect.inspect,
  });

  // ── Derived state ──────────────────────────────────────────

  const { context } = snapshot;
  const { activity, teams, rankEntries, tournamentRef, participantIds, tournamentFormat } = context;

  // Tournament integration
  const [liveTournamentSnapshot, setLiveTournamentSnapshot] =
    useState<ReturnType<TournamentActorRef["getSnapshot"]>>();

  useEffect(() => {
    if (!tournamentRef) {
      setLiveTournamentSnapshot(undefined);
      return;
    }

    setLiveTournamentSnapshot(tournamentRef.getSnapshot());

    const subscription = subscribeToTournament(tournamentRef, (nextSnapshot) => {
      setLiveTournamentSnapshot(nextSnapshot);
    });

    return () => subscription.unsubscribe();
  }, [tournamentRef]);

  const tournamentSnapshot = liveTournamentSnapshot;

  /** The current high-level phase name (flattened for convenience). */
  const phase = useMemo((): MatchFlowPhase => {
    if (snapshot.matches("idle")) return "idle";
    if (snapshot.matches("routing")) return "routing";
    if (snapshot.matches("setupTeams")) return "setupTeams";
    if (snapshot.matches("recording")) return "recording";
    if (snapshot.matches("tournament")) {
      if (tournamentRef && isTournamentDone(tournamentRef)) return "tournament.completed";
      return "tournament.inProgress";
    }
    if (snapshot.matches("reviewing")) return "reviewing";
    return "idle";
  }, [snapshot, tournamentRef, tournamentSnapshot]);

  /** Participants as Player objects. */
  const participants = useMemo(
    () => participantIds.map((id) => playersById[id]).filter(Boolean) as Player[],
    [participantIds, playersById],
  );

  /** Unassigned players (not yet on any team). */
  const unassigned = useMemo(
    () => participants.filter((p) => teams.every((t) => !t.includes(p.id))),
    [participants, teams],
  );

  /** Non-empty team indices. */
  const nonEmptyTeamIndices = useMemo(
    () => teams.flatMap((t, i) => (t.length > 0 ? [i] : [])),
    [teams],
  );

  /** Whether all teams have been ranked. */
  const allRanked = rankEntries.length === nonEmptyTeamIndices.length;

  /** Rank summary (grouped by rank for display). */
  const rankSummary = useMemo(() => groupByRank(rankEntries), [rankEntries]);

  /** Rank lookup by team index. */
  const rankOf = useCallback(
    (teamIndex: number) => rankEntries.find(([ti]) => ti === teamIndex)?.[1] ?? 0,
    [rankEntries],
  );

  /** Whether the team setup has enough teams to proceed. */
  const canConfirmTeams = snapshot.can({ type: "teams.confirm" });

  const activeTournamentMatch = useMemo(
    () => (tournamentRef ? getActiveTournamentMatch(tournamentRef) : undefined),
    [tournamentRef, tournamentSnapshot],
  );

  const tournamentStandings = useMemo(
    () => (tournamentRef ? getTournamentStandings(tournamentRef) : []),
    [tournamentRef, tournamentSnapshot],
  );

  const tournamentResults = useMemo(
    () => (tournamentRef ? getTournamentResults(tournamentRef) : []),
    [tournamentRef, tournamentSnapshot],
  );

  const canSubmitTournamentResults = tournamentResults.length > 0;

  // ── Actions ────────────────────────────────────────────────

  const selectActivity = useCallback(
    (activityId: ActivityId) => {
      const activity = activitiesById[activityId];
      if (!activity) return;

      const { participantIds } = activity;

      const pIds = participantIds.length > 0 ? participantIds : playerList.map((p) => p.id);

      const initialTeams = createTeamsForActivity(activity, pIds);

      send({
        type: "activity.select",
        activity,
        participantIds: pIds,
        teams: initialTeams,
        players: playersById,
      });
    },
    [activitiesById, playersById, send],
  );

  const setTeams = useCallback(
    (newTeams: Teams) => {
      send({ type: "teams.set", teams: newTeams });
    },
    [send],
  );

  const confirmTeams = useCallback(() => {
    send({ type: "teams.confirm" });
  }, [send]);

  const assignRank = useCallback(
    (teamIndex: number, tie: boolean = false) => {
      send({ type: "rank.assign", teamIndex, tie });
    },
    [send],
  );

  const undoRank = useCallback(
    (teamIndex: number) => {
      send({ type: "rank.undo", teamIndex });
    },
    [send],
  );

  const submitResults = useCallback(() => {
    if (!activity || !allRanked) return;
    send({ type: "results.submit" });
  }, [activity, allRanked, send]);

  const recordTournamentMatch = useCallback(
    (matchId: string, winnerId: PlayerId | "draw") => {
      if (activeTournamentMatch?.id !== matchId) return;

      send({ type: "tournament.recordResult", matchId, winnerId });
    },
    [activeTournamentMatch, send],
  );

  const submitTournamentResults = useCallback(() => {
    if (!activity || !canSubmitTournamentResults) return;
    send({ type: "results.submit" });
  }, [activity, canSubmitTournamentResults, send]);

  const confirmResults = useCallback(() => {
    if (!activity) return;

    if (isTournament(activity)) {
      if (!canSubmitTournamentResults) return;
      const matchTeams = tournamentResults.flatMap((tier: PlayerId[]) => tier.map((pid) => [pid]));
      const matchRanks = tournamentResults.flatMap((tier: PlayerId[], tierIndex: number) =>
        tier.map(() => tierIndex + 1),
      );
      recordMatch({
        activityId: activity.id,
        teams: matchTeams,
        ranks: matchRanks,
      });
    } else {
      if (!allRanked) return;
      const rankLookup = new Map(rankEntries);
      const matchTeams = nonEmptyTeamIndices.map((i) => teams[i]!);
      const matchRanks = nonEmptyTeamIndices.map((i) => rankLookup.get(i)!);
      if (matchRanks.some((r) => r == null || Number.isNaN(r))) return;
      recordMatch({
        activityId: activity.id,
        teams: matchTeams,
        ranks: matchRanks,
      });
    }

    send({ type: "results.confirm" });
  }, [
    activity,
    allRanked,
    rankEntries,
    nonEmptyTeamIndices,
    teams,
    canSubmitTournamentResults,
    tournamentResults,
    recordMatch,
    send,
  ]);

  const back = useCallback(() => {
    send({ type: "flow.back" });
  }, [send]);

  const reset = useCallback(() => {
    send({ type: "flow.reset" });
  }, [send]);

  // ── Public API ─────────────────────────────────────────────

  return {
    // State
    phase,
    activity,
    teams,
    participants,
    participantIds,
    unassigned,
    rankEntries,
    rankSummary,
    nonEmptyTeamIndices,
    allRanked,
    canConfirmTeams,
    tournamentRef,
    tournamentFormat,
    tournamentSnapshot,
    activeTournamentMatch,
    tournamentStandings,
    tournamentResults,
    canSubmitTournamentResults,
    snapshot,

    // Derived
    rankOf,

    // Actions
    selectActivity,
    setTeams,
    confirmTeams,
    assignRank,
    undoRank,
    submitResults,
    confirmResults,
    recordTournamentMatch,
    submitTournamentResults,
    back,
    reset,
  } as const;
};
